import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import AlignVerticalTopIcon from '@mui/icons-material/AlignVerticalTop';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatPaintOutlinedIcon from '@mui/icons-material/FormatPaintOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useDeckStore } from '../store/deckStore';
import type { ChartType, SlideElement } from '../types/slide';

const monoInput = { input: { sx: { fontFamily: '"JetBrains Mono", monospace', fontSize: 12 } } };

/** Textarea whose text is parsed into element data on every change, but kept locally so typing isn't disturbed. */
function ParsedField({
  label,
  helperText,
  initial,
  onParsed,
  mono,
  minRows = 4,
}: {
  label: string;
  helperText?: string;
  initial: string;
  onParsed: (text: string) => void;
  mono?: boolean;
  minRows?: number;
}) {
  const [text, setText] = useState(initial);
  return (
    <TextField
      label={label}
      helperText={helperText}
      multiline
      minRows={minRows}
      fullWidth
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onParsed(e.target.value);
      }}
      slotProps={mono ? monoInput : undefined}
    />
  );
}

const lines = (text: string) =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

const cells = (line: string) => line.split('|').map((c) => c.trim());

function ChartEditor({ el, edit }: { el: SlideElement; edit: (field: string, patch: Partial<SlideElement>) => void }) {
  const labels = el.chartLabels ?? [];
  const values = el.chartValues ?? [];
  const rows = Math.max(labels.length, values.length, 1);

  const setRow = (index: number, label: string, value: number) => {
    const nextLabels = [...labels];
    const nextValues = [...values];
    while (nextLabels.length < rows) nextLabels.push('');
    while (nextValues.length < rows) nextValues.push(0);
    nextLabels[index] = label;
    nextValues[index] = value;
    edit('chartData', { chartLabels: nextLabels, chartValues: nextValues });
  };

  const addRow = () =>
    edit('chartData', {
      chartLabels: [...labels, `Item ${labels.length + 1}`],
      chartValues: [...values, 0],
    });

  const removeRow = (index: number) =>
    edit('chartData', {
      chartLabels: labels.filter((_, i) => i !== index),
      chartValues: values.filter((_, i) => i !== index),
    });

  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={el.chartType ?? 'bar'}
        onChange={(_, v: ChartType | null) => v && edit('chartType', { chartType: v })}
        aria-label="Chart type"
        sx={{ flexWrap: 'wrap' }}
      >
        <ToggleButton value="bar" sx={{ flex: '1 1 30%' }}>
          Bar
        </ToggleButton>
        <ToggleButton value="hbar" sx={{ flex: '1 1 30%' }}>
          H-Bar
        </ToggleButton>
        <ToggleButton value="line" sx={{ flex: '1 1 30%' }}>
          Line
        </ToggleButton>
        <ToggleButton value="area" sx={{ flex: '1 1 30%' }}>
          Area
        </ToggleButton>
        <ToggleButton value="donut" sx={{ flex: '1 1 30%' }}>
          Donut
        </ToggleButton>
        <ToggleButton value="pie" sx={{ flex: '1 1 30%' }}>
          Pie
        </ToggleButton>
      </ToggleButtonGroup>

      <Typography variant="caption" color="text.secondary">
        Data points
      </Typography>
      <Stack spacing={1}>
        {Array.from({ length: rows }, (_, i) => (
          <Stack key={i} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <TextField
              size="small"
              label="Label"
              value={labels[i] ?? ''}
              onChange={(e) => setRow(i, e.target.value, Number(values[i]) || 0)}
              sx={{ flex: 1.4 }}
            />
            <TextField
              size="small"
              label="Value"
              type="number"
              value={values[i] ?? 0}
              onChange={(e) => setRow(i, labels[i] ?? '', Number(e.target.value) || 0)}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              aria-label={`Remove row ${i + 1}`}
              disabled={rows <= 1}
              onClick={() => removeRow(i)}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" color="neutral" startIcon={<AddIcon />} onClick={addRow} sx={{ flex: 1 }}>
          Add point
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="neutral"
          onClick={() =>
            edit('chartData', {
              chartLabels: ['W1', 'W2', 'W3', 'W4'],
              chartValues: [12, 18, 15, 22],
            })
          }
          sx={{ flex: 1 }}
        >
          Sample data
        </Button>
      </Stack>
      <ParsedField
        key={`${el.id}-bulk`}
        label="Bulk edit"
        helperText="Optional: paste label, value per line"
        mono
        minRows={3}
        initial={labels.map((l, i) => `${l}, ${values[i] ?? 0}`).join('\n')}
        onParsed={(text) => {
          const parsed = lines(text).map((l) => l.split(',').map((c) => c.trim()));
          if (!parsed.length) return;
          edit('chartData', {
            chartLabels: parsed.map((r) => r[0] ?? ''),
            chartValues: parsed.map((r) => Number(r[1]) || 0),
          });
        }}
      />
    </Stack>
  );
}

export function ElementSettings({ el }: { el: SlideElement }) {
  const updateElement = useDeckStore((s) => s.updateElement);
  const pushHistory = useDeckStore((s) => s.pushHistory);
  const removeElement = useDeckStore((s) => s.removeElement);
  const removeSelectedElements = useDeckStore((s) => s.removeSelectedElements);
  const duplicateElement = useDeckStore((s) => s.duplicateElement);
  const alignSelected = useDeckStore((s) => s.alignSelected);
  const distributeSelected = useDeckStore((s) => s.distributeSelected);
  const duplicateStyles = useDeckStore((s) => s.duplicateStyles);
  const selectedCount = useDeckStore((s) => s.selectedElementIds.length);

  const edit = (field: string, patch: Partial<SlideElement>) => {
    pushHistory(`edit:${el.id}:${field}`);
    updateElement(el.id, patch);
  };

  const numberField = (key: 'x' | 'y' | 'w' | 'h', label: string) => (
    <TextField
      label={label}
      type="number"
      value={Math.round(el[key])}
      onChange={(e) => {
        const v = Number(e.target.value);
        if (Number.isFinite(v)) edit(key, { [key]: key === 'w' || key === 'h' ? Math.max(16, v) : v });
      }}
      slotProps={{ htmlInput: { step: 1 } }}
    />
  );

  const ALIGN = [
    ['left', 'Align left', AlignHorizontalLeftIcon],
    ['center', 'Align center', AlignHorizontalCenterIcon],
    ['right', 'Align right', AlignHorizontalRightIcon],
    ['top', 'Align top', AlignVerticalTopIcon],
    ['middle', 'Align middle', AlignVerticalCenterIcon],
    ['bottom', 'Align bottom', AlignVerticalBottomIcon],
  ] as const;

  return (
    <Stack spacing={2}>
      {selectedCount > 1 && (
        <Typography variant="caption" color="text.secondary">
          {selectedCount} selected · Shift-click to add · styles copy from the last selected
        </Typography>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          color="neutral"
          startIcon={<ContentCopyOutlinedIcon />}
          onClick={() => duplicateElement(el.id)}
          sx={{ flex: 1 }}
        >
          Duplicate
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => (selectedCount > 1 ? removeSelectedElements() : removeElement(el.id))}
          sx={{ flex: 1 }}
        >
          Delete
        </Button>
      </Stack>

      {selectedCount > 1 && (
        <Button
          size="small"
          variant="outlined"
          color="neutral"
          startIcon={<FormatPaintOutlinedIcon />}
          onClick={() => duplicateStyles()}
        >
          Copy style to selection
        </Button>
      )}

      {selectedCount === 1 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
          {numberField('x', 'X')}
          {numberField('y', 'Y')}
          {numberField('w', 'Width')}
          {numberField('h', 'Height')}
        </Box>
      )}

      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        {ALIGN.map(([edge, label, Icon]) => (
          <Tooltip key={edge} title={selectedCount > 1 ? `${label} (selection)` : label}>
            <IconButton size="small" aria-label={label} onClick={() => alignSelected(edge)}>
              <Icon fontSize="small" />
            </IconButton>
          </Tooltip>
        ))}
      </Stack>

      {selectedCount >= 3 && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            color="neutral"
            onClick={() => distributeSelected('horizontal')}
            sx={{ flex: 1 }}
          >
            Distribute H
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="neutral"
            onClick={() => distributeSelected('vertical')}
            sx={{ flex: 1 }}
          >
            Distribute V
          </Button>
        </Stack>
      )}

      {selectedCount === 1 && el.type === 'text' && (
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={el.align ?? 'left'}
          onChange={(_, v: 'left' | 'center' | 'right' | null) => v && edit('align', { align: v })}
          aria-label="Text alignment"
        >
          <ToggleButton value="left" aria-label="Left">
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="center" aria-label="Center">
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" aria-label="Right">
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {selectedCount === 1 && el.type === 'kpi_card' && (
        <Stack spacing={1.5}>
          <TextField label="Metric" value={el.metric ?? ''} onChange={(e) => edit('metric', { metric: e.target.value })} />
          <TextField label="Label" value={el.label ?? ''} onChange={(e) => edit('label', { label: e.target.value })} />
          <TextField
            label="Change"
            helperText="e.g. +0.4pp, optional"
            value={el.delta ?? ''}
            onChange={(e) => edit('delta', { delta: e.target.value })}
          />
        </Stack>
      )}

      {selectedCount === 1 && el.type === 'chart' && <ChartEditor el={el} edit={edit} />}

      {selectedCount === 1 && el.type === 'timeline' && (
        <ParsedField
          key={el.id}
          label="Milestones"
          helperText="One per line: date | title | detail"
          mono
          minRows={5}
          initial={(el.milestones ?? []).map((m) => `${m.date} | ${m.title} | ${m.detail}`).join('\n')}
          onParsed={(text) =>
            edit('milestones', {
              milestones: lines(text).map((l) => {
                const [date = '', title = '', detail = ''] = cells(l);
                return { date, title, detail };
              }),
            })
          }
        />
      )}

      {selectedCount === 1 && el.type === 'process_row' && (
        <ParsedField
          key={el.id}
          label="Steps"
          helperText="One per line: title | description"
          mono
          minRows={4}
          initial={(el.steps ?? []).map((st) => `${st.title} | ${st.body}`).join('\n')}
          onParsed={(text) =>
            edit('steps', {
              steps: lines(text).map((l) => {
                const [title = '', body = ''] = cells(l);
                return { title, body };
              }),
            })
          }
        />
      )}

      {selectedCount === 1 && el.type === 'pros_cons' && (
        <Stack spacing={1.5}>
          <ParsedField
            key={`${el.id}-pros`}
            label="Pros"
            helperText="One per line"
            initial={(el.pros ?? []).join('\n')}
            onParsed={(text) => edit('pros', { pros: lines(text) })}
          />
          <ParsedField
            key={`${el.id}-cons`}
            label="Cons"
            helperText="One per line"
            initial={(el.cons ?? []).join('\n')}
            onParsed={(text) => edit('cons', { cons: lines(text) })}
          />
        </Stack>
      )}

      {selectedCount === 1 && el.type === 'callout' && (
        <ParsedField
          key={el.id}
          label="Callout text"
          minRows={3}
          initial={el.text ?? ''}
          onParsed={(text) => edit('text', { text })}
        />
      )}

      {selectedCount === 1 && el.type === 'quote_block' && (
        <Stack spacing={1.5}>
          <ParsedField
            key={`${el.id}-quote`}
            label="Quote"
            minRows={3}
            initial={el.text ?? ''}
            onParsed={(text) => edit('text', { text })}
          />
          <TextField
            label="Attribution"
            value={el.attribution ?? ''}
            onChange={(e) => edit('attribution', { attribution: e.target.value })}
          />
        </Stack>
      )}

      {selectedCount === 1 && el.type === 'checklist' && (
        <ParsedField
          key={el.id}
          label="Checklist items"
          helperText="One per line"
          minRows={5}
          initial={(el.items ?? []).join('\n')}
          onParsed={(text) => edit('items', { items: lines(text) })}
        />
      )}

      {selectedCount === 1 && el.type === 'stat_row' && (
        <ParsedField
          key={el.id}
          label="Stats"
          helperText="One per line: metric | label"
          mono
          minRows={4}
          initial={(el.stats ?? []).map((s) => `${s.metric} | ${s.label}`).join('\n')}
          onParsed={(text) =>
            edit('stats', {
              stats: lines(text).map((l) => {
                const [metric = '', label = ''] = cells(l);
                return { metric, label };
              }),
            })
          }
        />
      )}

      {selectedCount === 1 && el.type === 'team_card' && (
        <Stack spacing={1.5}>
          <TextField label="Name" value={el.text ?? ''} onChange={(e) => edit('text', { text: e.target.value })} />
          <TextField label="Role" value={el.role ?? ''} onChange={(e) => edit('role', { role: e.target.value })} />
          <TextField
            label="Contact"
            value={el.contact ?? ''}
            onChange={(e) => edit('contact', { contact: e.target.value })}
          />
        </Stack>
      )}
    </Stack>
  );
}
